/**
 * La home de `/interfaz-v2` — la home REAL de la plataforma en desktop.
 *
 * Salió del sandbox de variantes como "versión 2" y ganó la comparación
 * (ADR-2026-09-01-la-version-2-es-la-home; supersede
 * ADR-2026-08-31-home-tres-variantes-imagen-video, que montaba tres homes en paralelo
 * bajo `/interfaz-v2-version1|2|3`). Las otras dos versiones y la carpeta
 * `home-sandbox/` se borraron con esa decisión.
 *
 * ── Qué hace ─────────────────────────────────────────────────────────────────
 *
 * Abre con el saludo y los accesos de cuenta, y después UNA de dos cabeceras
 * (ADR-2026-10-07-la-home-elige-su-cabecera; lo decide el shell por `grupo_testing`):
 *
 *  • `CabeceraPorDestino` — grupo C: tres cards por destino ("Redes sociales",
 *    "Catálogos", "Anuncios") con los logos de sus plataformas en abanico, que abren un
 *    popup con los modos de esa sección (ADR-2026-09-29-la-home-se-parte-por-destino).
 *    Sin el Agente.
 *  • `CabeceraPorVertiente` — el resto: las dos cards "Crear imágenes" / "Crear videos"
 *    con su popup, la barra que presenta el Agente y, en los rubros sin catálogo propio,
 *    el hero del Agente en lugar de las cards. Es la home de `principal` tal cual.
 *
 * En el teléfono sigue como estaba.
 *
 * Debajo de las secciones van las **ediciones rápidas**, en lista de cuatro columnas
 * con la pastilla a la izquierda en vez de las tarjetas del pie
 * (ADR-2026-08-31-version2-toma-los-atajos-de-la-version3) — ver `EdicionesRapidasHero`
 * acá abajo. De ahí para abajo es la home de siempre: creaciones recientes y
 * plantillas.
 *
 * Es PRESENTACIONAL: los modos, las plantillas y sus handlers entran por props desde
 * `InterfazV2`. Lo único que decide es qué subconjunto muestra. El esqueleto que se ve
 * mientras carga es `HomeMinimalSkeleton`, en `EstiloMinimal` — si cambia el layout de
 * acá arriba, tiene que cambiar con él (ADR-2026-08-03-home-aparece-entera-o-skeleton).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { normalizeBrandHexColors } from '../edicionGrafica/lib/brandColors';
import { EnlaceApp } from '../../components/EnlaceApp';
import { useNavigate } from 'react-router-dom';
import {
  AvatarUsuario,
  EDICIONES_RAPIDAS,
  GRIS_SHELL,
  HomeMinimalSkeleton,
  MenuCuenta,
  SeccionesHome,
  TEMAS,
  nombrePila,
  useCuentaShell,
  type FilaPlantillasHome,
  type ModoHome,
  type VarianteMinimal,
} from './EstiloMinimal';
import {
  ModalQRWhatsApp,
  urlsWhatsApp,
  MENSAJE_SOPORTE,
} from '../../components/ContactarPorWhatsApp';
import { CampanaNotificaciones } from '../../components/notificaciones/CampanaNotificaciones';
import { saludo } from './contenidoAtlas';
import { Icon } from '../../design-system/icons';
import { CardAcademyHome } from '../academia/CardAcademyHome';
// Las dos cabeceras que conviven (ADR-2026-10-07-la-home-elige-su-cabecera): por destino
// para el grupo C, por vertiente —la de `principal`— para el resto.
import { CabeceraPorDestino } from './CabeceraPorDestino';
import { IconoDeHerramienta } from './SeccionesDeLaSidebar';
import { CabeceraPorVertiente } from './CabeceraPorVertiente';
// La estela de la home del Agente (sólo con la cabecera de vertientes).
import { EstelaDelAgente } from '../v2/components/EstelaDelAgente';
// El saludo es un componente y no un nodo armado acá: ahora lo monta también el hero
// mobile desde `InterfazV2` (ADR-2026-09-21-el-saludo-tambien-va-en-el-telefono).
import { SaludoDeLaHome } from '../v2/components/SaludoDeLaHome';

/**
 * "Ediciones rápidas" como lista — ADR-2026-08-31-version2-toma-los-atajos-de-la-version3,
 * reubicada por ADR-2026-09-04-ediciones-rapidas-debajo-de-recientes.
 *
 * Antes estas siete acciones CERRABAN la home, en tarjetas de 4×2 con ícono centrado,
 * borde y fondo (es lo que sigue dibujando `SeccionesHome` cuando no se le pasa
 * `sinEdicionesRapidas`, que es como las muestra la home de mobile). Acá cambian de
 * anatomía: lista de cuatro columnas, ícono en una pastilla a la IZQUIERDA y el nombre
 * + la descripción a la derecha, sin tarjeta. Y viven DEBAJO de "Creaciones recientes",
 * en el slot que `SeccionesHome` expone para eso.
 *
 * El porqué del cambio de forma: compiten con las cards de sección (redes, catálogos,
 * anuncios), que son grandes y con abanico de logos. Si fueran tarjetas,
 * la home encadenaría tres bloques de tarjetas y la jerarquía se aplanaría
 * —lo que manda es elegir el medio—. Como lista sin caja se leen de un vistazo y
 * quedan claramente en segundo plano, pero mantienen la descripción, que es lo que
 * explica qué hace cada una.
 *
 * La pastilla existe porque sin fondo de tarjeta un ícono gris sobre el gris del shell
 * desaparecía, pero va en el azul de Atlas **suave** —`#EBEFFE` de fondo, ícono en
 * `brand-primary`, la misma pastilla que la de créditos del encabezado— y no en el azul
 * lleno: siete pastillas saturadas arriba de todo pesaban más que las cards de
 * sección, que es lo que tiene que mandar.
 *
 * Las acciones y sus destinos salen de `EDICIONES_RAPIDAS`, la misma tabla que dibuja el
 * bloque de mobile: acá cambia la presentación, no la lista.
 */
function EdicionesRapidasHero() {
  const navigate = useNavigate();
  const { uiNueva, herramientas, abrirPricing } = useCuentaShell();
  // En la interfaz nueva, la MISMA lista que el panel "Herramientas" de la sidebar
  // (ediciones rápidas + Recrear imagen, Estudio de Video, Diseño, Flujos) y con su
  // pastilla gris: las dos ofrecen lo mismo y se ven igual.
  if (uiNueva && herramientas.length > 0) {
    return (
      <section className="mt-10">
        {/* "Herramientas" y no "Ediciones rápidas": la lista suma Estudio de Video, Flujos y
            Diseño, que no son ediciones. Es el nombre de la sección en la sidebar. */}
        <h2 className="text-[26px] font-bold tracking-[-0.01em]">Herramientas</h2>
        <p className="mt-1 text-[15px] text-black/45">
          Para editar, animar y seguir trabajando lo que creaste
        </p>
        <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          {herramientas.map((f) => (
            <EnlaceApp
              key={f.id}
              // Con candado no navega: abre los planes, como en la sidebar.
              href={f.bloqueada ? undefined : f.ruta}
              onNavegar={() => (f.bloqueada ? abrirPricing('bloqueado') : navigate(f.ruta))}
              className="group -m-2 flex items-center gap-3.5 rounded-2xl p-2 text-left transition-colors hover:bg-black/[0.035]"
            >
              <span className="relative grid h-[46px] w-[46px] shrink-0 place-items-center overflow-hidden rounded-2xl bg-[#F2F3F7] text-black/60 ring-1 ring-black/[0.04] transition-colors group-hover:bg-[#E9EBF1]">
                <IconoDeHerramienta fila={f} tamano={20} />
                {f.bloqueada && (
                  <span aria-hidden="true" className="absolute inset-0 grid place-items-center bg-black/45 text-white">
                    <span className="material-symbols-rounded text-[16px]">lock</span>
                  </span>
                )}
              </span>
              <span className="min-w-0">
                <span className={`block truncate text-[15px] font-semibold tracking-[-0.01em] ${f.bloqueada ? 'text-black/45' : ''}`}>
                  {f.nombre}
                </span>
                <span className="mt-0.5 block truncate text-[13px] text-black/45">
                  {f.bloqueada ? 'Incluido en los planes pagos' : f.bajada}
                </span>
              </span>
            </EnlaceApp>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className="mt-10">
      <h2 className="text-[26px] font-bold tracking-[-0.01em]">Ediciones rápidas</h2>
      <p className="mt-1 text-[15px] text-black/45">
        Herramientas para editar tus imágenes y videos
      </p>

      {/* Cuatro columnas en desktop —las siete acciones entran en dos filas—; a `sm`
          caen a dos y en teléfono a una, porque la fila es ícono + dos renglones de
          texto y más angosta que eso el nombre se corta. */}
      <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {EDICIONES_RAPIDAS.map(({ label, desc, Icono, ruta }) => (
          <EnlaceApp
            key={label}
            // El atajo YA tiene su ruta (`/atajo/:accion`): con el `href` puesto,
            // Cmd/Ctrl+clic lo abre en otra pestaña
            // (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links).
            href={ruta}
            onNavegar={() => navigate(ruta)}
            // Sin tarjeta: el hover pinta un rectángulo apenas más grande que la
            // fila (`-m-2 p-2`) para que se note qué se está por clickear.
            className="group -m-2 flex items-center gap-3.5 rounded-2xl p-2 text-left transition-colors hover:bg-black/[0.035]"
          >
            <span className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-2xl bg-[#EBEFFE] text-brand-primary transition-colors group-hover:bg-[#DFE7FD]">
              <Icono className="h-[20px] w-[20px]" strokeWidth={1.8} />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[15px] font-semibold tracking-[-0.01em]">
                {label}
              </span>
              <span className="mt-0.5 block truncate text-[13px] text-black/45">
                {desc}
              </span>
            </span>
          </EnlaceApp>
        ))}
      </div>
    </section>
  );
}

// ── Halo de marca (colores de la paleta activa, como CabeceraDeMarca) ───────

function usePaletaDeMarca(): string[] {
  const { activeBrand } = useBrandProfile();
  const brandId = activeBrand?.id ?? null;
  const [paleta, setPaleta] = useState<string[]>([]);
  useEffect(() => {
    if (!brandId) return;
    let cancelado = false;
    supabase
      .from('brand_profiles')
      .select('colors')
      .eq('id', brandId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelado) return;
        setPaleta(normalizeBrandHexColors(data?.colors));
      });
    return () => { cancelado = true; };
  }, [brandId]);
  return paleta;
}

const coloresDelHalo = (paleta: string[]): [string, string, string] => {
  const hex6 = (h: string) => (h.startsWith('#') ? h : `#${h}`).padEnd(7, '0');
  const utiles = paleta
    .map((hex) => {
      const h = hex6(hex);
      const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      const sat = max === min ? 0 : (max - min) / (1 - Math.abs(2 * l - 1));
      return { h, sat, l };
    })
    .filter((c) => c.sat > 0.2 && c.l > 0.1 && c.l < 0.92)
    .sort((a, b) => b.sat - a.sat)
    .map((c) => c.h);
  if (utiles.length === 0) return ['#3D5CFF', '#8EA2FF', '#C9D3FF'];
  return [utiles[0]!, utiles[1] ?? utiles[0]!, utiles[2] ?? utiles[1] ?? utiles[0]!];
};

const MASCARA_DEL_HALO =
  'linear-gradient(to bottom, black 35%, transparent), linear-gradient(to right, transparent, black 14%, black 86%, transparent)';

export function HomeMinimal({
  variante = 'claro',
  modos,
  plantillas,
  totalPlantillas,
  banners,
  cargando = false,
  uiNueva = false,
  abreConElAgente = false,
  agenteBloqueado = false,
  conPresentacionDelAgente = false,
  onVerTodasPlantillas,
  hrefTodasPlantillas,
}: {
  variante?: VarianteMinimal;
  modos: ModoHome[];
  plantillas: FilaPlantillasHome[];
  totalPlantillas?: number;
  banners?: ReactNode;
  cargando?: boolean;
  /**
   * La cabecera por destino (grupo C) en vez de la de vertientes
   * (ADR-2026-10-07-la-home-elige-su-cabecera). La decide el shell (`CuentaCtx.uiNueva`)
   * y entra por props como todo lo demás: esta pantalla sigue sin decidir nada.
   */
  uiNueva?: boolean;
  /**
   * SÓLO en la cabecera de vertientes. La home abre con el AGENTE y no con las dos cards
   * (ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente). Lo decide el rubro
   * de la marca (`homeAbreConElAgente`) y lo resuelve `InterfazV2`: esta pantalla sigue
   * sin saber nada de rubros, sólo qué subconjunto muestra.
   *
   * Las dos ramas se excluyen con la de abajo: o el hero ES el Agente, o hay que
   * presentarlo. Nunca las dos (`homeMuestraPresentacionDelAgente` es la negación de
   * `homeAbreConElAgente`).
   */
  abreConElAgente?: boolean;
  /** El plan no incluye el Agente: la caja del hero manda a los planes. */
  agenteBloqueado?: boolean;
  /**
   * SÓLO en la cabecera de vertientes. Se dibuja la barra que PRESENTA el Agente, arriba
   * de "Elegí qué querés crear" (ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final).
   * Lo decide el rubro de la marca (`homeMuestraPresentacionDelAgente`) y lo resuelve
   * `InterfazV2`. En la home del Agente el hero ya es el Agente, así que la franja no
   * se dibuja en esa rama aunque la prop venga en `true`. Por defecto NO se dibuja.
   */
  conPresentacionDelAgente?: boolean;
  onVerTodasPlantillas: () => void;
  /** La URL del catálogo de plantillas: hace enlaces al "Ver todo" y al cierre de la
   *  sección (ADR-2026-09-08-las-cards-de-la-home-tambien-son-links). */
  hrefTodasPlantillas?: string;
}) {
  const tema = TEMAS[variante];
  const {
    creditos,
    planLabel,
    nombreCompleto,
    idCompany,
    idMarca,
    cargandoCuenta,
    conFadeDeSalida,
    abrirPricing,
    rubroMarca,
  } = useCuentaShell();
  // Modal del QR de soporte (ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio).
  // El estado vive acá, al lado del botón que lo abre, y no en el shell: es un detalle
  // de este encabezado, nadie más lo dispara.
  const [qrWhatsApp, setQRWhatsApp] = useState(false);
  const navigate = useNavigate();
  const nombre = nombrePila(nombreCompleto);

  /**
   * En la home del Agente (sólo con la cabecera de vertientes) el saludo se va de la fila
   * del encabezado y baja al hero, CENTRADO arriba del switch
   * (ADR-2026-09-21-el-saludo-se-centra-arriba-del-switch). Con la fila vacía de texto,
   * los accesos quedan solos y suben.
   */
  const saludoEnElHero = !uiNueva && abreConElAgente;

  // Halo de marca: colores de la paleta activa, como en /mi-marca.
  const paleta = usePaletaDeMarca();
  const [c1, c2, c3] = coloresDelHalo(paleta);
  const halo = [
    `radial-gradient(46% 100% at 4% 0%, ${c1}38 0%, transparent 70%)`,
    `radial-gradient(40% 85% at 42% 0%, ${c2}2e 0%, transparent 70%)`,
    `radial-gradient(44% 95% at 92% 0%, ${c3}33 0%, transparent 70%)`,
  ].join(', ');

  if (cargando || cargandoCuenta) return <HomeMinimalSkeleton uiNueva={uiNueva} />;

  /** Abrir un modo: el mismo reparto que la tira (ADR-2026-08-03-modo-bloqueado-abre-pricing). */
  const abrirModo = (m: ModoHome) => {
    if (m.bloqueado) {
      // Con ejemplos cargados manda el llamador: su `onAbrir` abre el VISOR de
      // ejemplos —lo que hace el modo, la nota del plan y "Mejorar plan"—, que es lo
      // mismo que hacen `/modos` y el sheet de Generar al tocar una card con candado
      // (ADR-2026-09-04-el-modo-bloqueado-del-popup-muestra-sus-ejemplos). Sin
      // ejemplos sigue derecho a planes, como desde
      // ADR-2026-08-03-modo-bloqueado-abre-pricing: un visor vacío no dice nada.
      //
      // SIN `conFadeDeSalida`: el visor es un overlay sobre esta misma pantalla y no
      // una navegación; apagar la home dejaría el modal flotando sobre nada.
      if (m.conEjemplos) {
        m.onAbrir();
        return;
      }
      abrirPricing('bloqueado');
      return;
    }
    conFadeDeSalida(m.onAbrir);
  };

  return (
    /* `relative isolate` SÓLO en la home del Agente: es lo que deja que la estela se
       cuelgue del techo con `-z-10` sin irse detrás del fondo de un ancestro, y lo que
       la encierra acá adentro. En las otras homes no hay estela, y estrenar un contexto
       de apilado para nada puede cambiarle el orden a algo que ya andaba. */
    <div className={`aparece-tras-esqueleto ${saludoEnElHero ? 'relative isolate' : 'relative isolate'}`}>
      {/* Halo de marca — mismo patrón que CabeceraDeMarca de /mi-marca: tres
          manchas radiales con los colores más saturados de la paleta de la marca
          activa, que se desvanecen hacia abajo y hacia los costados con máscara. */}
      <div
        key={paleta.join(',') || 'sin-paleta'}
        aria-hidden
        className="pointer-events-none absolute -inset-x-4 -top-8 -z-10 h-[380px] sm:-inset-x-8 animate-[aparecer-suave_0.8s_ease-out_both]"
        style={{
          background: halo,
          WebkitMaskImage: MASCARA_DEL_HALO,
          maskImage: MASCARA_DEL_HALO,
          WebkitMaskComposite: 'source-in',
          maskComposite: 'intersect',
        }}
      />
      {/* La estela se monta ACÁ, en la raíz de la home, y no adentro del hero: la luz
          tiene que arrancar en el techo, arriba de los accesos de cuenta
          (ADR-2026-09-21-la-estela-arranca-en-el-techo-de-la-home). */}
      {saludoEnElHero && <EstelaDelAgente />}
      {/* ── Saludo + accesos de cuenta ────────────────────────────────────────
          Comparten renglón: el saludo a la izquierda y los accesos a la derecha,
          centrados entre sí. En la home del Agente el saludo baja al hero y la fila
          queda sólo con los accesos, a la derecha. */}
      <div
        className={`flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6 ${
          saludoEnElHero ? 'sm:justify-end' : 'sm:justify-between'
        }`}
      >
        {!saludoEnElHero && (
          <SaludoDeLaHome
            nombre={nombre}
            tamano="encabezado"
            // En la home nueva las cards son por destino (redes, catálogos, anuncios): la
            // pregunta dice qué se crea en ellas, que con sólo el destino no se entendía.
            pregunta={uiNueva ? '¿Qué imágenes o videos creamos hoy?' : undefined}
          />
        )}

        <div className="flex shrink-0 items-center gap-2.5 text-[13px]">
          {/* "Invitá y ganá" — ADR-2026-08-27-programa-referidos. A la IZQUIERDA de la
              pastilla de créditos porque es lo que la llena: el programa paga en
              créditos, así que el control que los consigue va pegado al que los muestra.

              **El azul de la fila es éste**, no el saldo —
              ADR-2026-08-31-el-enfasis-de-la-fila-pasa-a-invita-y-gana. Puede haber UN
              solo elemento enfático en la fila: el saldo es un dato que el usuario ya
              conoce y que la app le repite en la sidebar y en cada pantalla; el programa
              de referidos es una OFERTA que sólo se ve acá.

              La etiqueta aparece recién en `lg`: entre 768 y 1024 la fila lleva créditos +
              campana + Whatsapp + avatar, y la etiqueta la empuja contra el saludo. */}
          <button
            type="button"
            title="Invitá y ganá"
            aria-label="Invitá y ganá"
            onClick={() => conFadeDeSalida(() => navigate('/referidos'))}
            className="flex h-[34px] items-center gap-1.5 rounded-xl bg-[#EBEFFE] px-3 text-[12.5px] font-semibold text-brand-primary ring-1 ring-[rgba(22,68,235,0.22)] transition-colors hover:bg-[#DFE7FD] hover:ring-[rgba(22,68,235,0.34)] lg:px-3.5"
          >
            <span className="material-symbols-rounded shrink-0 text-[18px]">redeem</span>
            <span className="hidden lg:inline">Invitá y ganá</span>
          </button>

          <button
            type="button"
            title={`${planLabel} — ver tu suscripción`}
            aria-label={`${creditos} ${creditos === 1 ? 'crédito' : 'créditos'} — ver tu suscripción`}
            onClick={() =>
              conFadeDeSalida(() =>
                navigate('/configuracion', { state: { section: 'plan' } }),
              )
            }
            className={`flex h-[34px] items-center gap-1.5 rounded-xl px-3.5 text-[12.5px] font-[500] ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] ${GRIS_SHELL} ${tema.tarjeta}`}
          >
            {/* El ícono de token queda: es azul por sí mismo, así que la pastilla gris
                no pierde la referencia visual a los créditos — sólo deja de gritar. */}
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="h-3.5 w-3.5"
            />
            {/* Sólo el número: se fue la etiqueta "Créditos"
                (ADR-2026-08-31-el-enfasis-de-la-fila-pasa-a-invita-y-gana). El ícono ya
                dice de qué es el número, y la fila necesitaba el ancho para la campana.
                El texto sigue para lectores de pantalla en el `aria-label`. */}
            <span className="tabular-nums text-[13px]">{creditos}</span>
          </button>

          {/* En un teléfono, WhatsApp directo; en una compu, el modal del QR —
              ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio. `wa.me` en
              desktop abre WhatsApp Web, que mucha gente no tiene logueada. El corte va
              en `md` (768), con las mismas dos ramas por CSS que usa
              `ContactarPorWhatsApp`. Sin etiqueta "Whatsapp"
              (ADR-2026-08-31-el-enfasis-de-la-fila-pasa-a-invita-y-gana): la fila sumó
              la campana y de los cuatro controles éste es el que mejor sobrevive sin
              texto — el logo se reconoce solo. */}
          <a
            href={urlsWhatsApp({ mensaje: MENSAJE_SOPORTE }).directa}
            target="_blank"
            rel="noopener noreferrer"
            title="Escribinos por WhatsApp"
            aria-label="Escribinos por WhatsApp"
            className={`flex h-[34px] w-[34px] items-center justify-center rounded-xl ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] md:hidden ${GRIS_SHELL} ${tema.tarjeta}`}
          >
            <Icon name="solid/whatsapp" size={18} className="shrink-0" />
          </a>
          <button
            type="button"
            onClick={() => setQRWhatsApp(true)}
            title="Escribinos por WhatsApp"
            aria-label="Escribinos por WhatsApp"
            className={`hidden h-[34px] w-[34px] items-center justify-center rounded-xl ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] md:flex ${GRIS_SHELL} ${tema.tarjeta}`}
          >
            <Icon name="solid/whatsapp" size={18} className="shrink-0" />
          </button>
          <ModalQRWhatsApp
            abierto={qrWhatsApp}
            onCerrar={() => setQRWhatsApp(false)}
            mensaje={MENSAJE_SOPORTE}
            titulo="Hablá con soporte"
          />

          {/* Campana — ADR-2026-08-31-centro-de-notificaciones. Va PEGADA al avatar y
              en el extremo derecho de la fila: es la convención de toda herramienta con
              un centro de notificaciones, y el usuario la busca ahí antes de leer la
              fila. Los otros tres controles son destinos —referidos, suscripción,
              soporte—; la campana y el avatar son la cuenta. `conFadeDeSalida` como el
              resto de la fila: sin eso, abrir una notificación sería la única
              navegación del encabezado que corta la pantalla de golpe. */}
          <CampanaNotificaciones
            variante="encabezado"
            onNavegar={(ruta) => conFadeDeSalida(() => navigate(ruta))}
          />

          <MenuCuenta variante="home">
            <AvatarUsuario
              px={34}
              className="!bg-white transition-shadow hover:ring-black/[0.14]"
            />
          </MenuCuenta>
        </div>
      </div>

      {banners}

      {uiNueva ? (
        /* ── Secciones por DESTINO (grupo C) ───────────────────────────────
           Tres cards —redes, catálogos, anuncios— que abren el popup con los modos de
           esa sección (ADR-2026-09-29-la-home-se-parte-por-destino). Academia Atlas va
           ARRIBA de las cards en esta home. */
        <>
        <CardAcademyHome className="mt-8" />
        <CabeceraPorDestino
          modos={modos}
          rubroMarca={rubroMarca}
          abrirModo={abrirModo}
          irA={(ruta) => conFadeDeSalida(() => navigate(ruta))}
        />
        </>
      ) : (
        /* ── Vertientes (todo grupo que no es C) ────────────────────────────
           Las dos cards "Crear imágenes" / "Crear videos" con su popup, la barra que
           presenta el Agente y, en los rubros sin catálogo propio, el hero del Agente en
           lugar de las cards. Es la home de `principal` tal cual
           (ADR-2026-10-07-la-home-elige-su-cabecera). */
        <CabeceraPorVertiente
          modos={modos}
          rubroMarca={rubroMarca}
          abrirModo={abrirModo}
          abreConElAgente={abreConElAgente}
          agenteBloqueado={agenteBloqueado}
          conPresentacionDelAgente={conPresentacionDelAgente}
          saludo={<SaludoDeLaHome nombre={nombre} tamano="hero" />}
          onIrAPlanes={() => abrirPricing('bloqueado')}
        />
      )}

      {/* Academia Atlas, debajo de las herramientas
          (ADR-2026-10-07-la-card-de-academia-va-debajo-de-las-herramientas). Fuera del
          ternario a propósito: en las dos cabeceras cae debajo de lo que se usa para
          crear —las cards de destino, las de vertiente o el hero del Agente—. */}
      {!uiNueva && <CardAcademyHome className="mt-8" />}

      {/* De acá para abajo, lo MISMO que muestra la home de mobile
          (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar), salvo el slot: los
          atajos se cuelan entre "Creaciones recientes" y Plantillas
          (ADR-2026-09-04-ediciones-rapidas-debajo-de-recientes). Siguen sin ir al
          pie —`sinEdicionesRapidas` evita que se dibujen dos veces— y conservan su
          anatomía de lista, que la define esta home. */}
      <SeccionesHome
        idCompany={idCompany}
        idMarca={idMarca}
        plantillas={plantillas}
        totalPlantillas={totalPlantillas}
        onVerTodasPlantillas={onVerTodasPlantillas}
        hrefTodasPlantillas={hrefTodasPlantillas}
        variante={variante}
        sinEdicionesRapidas
        slotDespuesDeRecientes={<EdicionesRapidasHero />}
      />
    </div>
  );
}

export default HomeMinimal;
